import React, { useState, useEffect } from "react";
import StatCards from "../StatCards";
import ApplicationsTable from "./ApplicationsTable";
import { bankingPartnerAPI } from "../../../services/AdminPortal/api";
import { toast } from "react-toastify";

export default function DashboardTab({ partnerData }) {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState({
    approved: 0,
    processing: 0,
    rejected: 0,
  });
  const [dashboardStats, setDashboardStats] = useState(null);

  // Fetch applications for this specific banking partner
  const fetchApplications = async () => {
    if (!partnerData?._id) return;

    setLoading(true);
    setError(null);
    try {
      const result = await bankingPartnerAPI.getApplicationsByBankId(
        partnerData._id
      );
      if (result.success) {
        setApplications(result.applications || result.data || []);

        // Calculate summary from applications
        const appSummary = {
          approved: 0,
          processing: 0,
          rejected: 0,
        };

        (result.applications || result.data || []).forEach((app) => {
          const status = app.status?.toLowerCase();
          if (status === "approved") {
            appSummary.approved++;
          } else if (status === "processing" || status === "in-review") {
            appSummary.processing++;
          } else if (status === "rejected") {
            appSummary.rejected++;
          }
        });

        setSummary(appSummary);
      }
    } catch (error) {
      console.error("Error fetching applications:", error);
      setError(error.message || "Failed to fetch applications");
      toast.error(error.message || "Failed to fetch applications");
    } finally {
      setLoading(false);
    }
  };

  // Fetch dashboard statistics
  const fetchDashboardStats = async () => {
    if (!partnerData?._id) return;

    try {
      const result = await bankingPartnerAPI.getDashboardAnalytics(
        partnerData._id
      );
      if (result.success) {
        setDashboardStats(result.data);
      }
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
      // Fallback to basic stats calculation
      try {
        const basicResult = await bankingPartnerAPI.getDashboardCardStats(
          partnerData._id
        );
        if (basicResult.success) {
          setDashboardStats(basicResult.data);
        }
      } catch (fallbackError) {
        console.error("Error fetching basic stats:", fallbackError);
      }
    }
  };

  useEffect(() => {
    fetchApplications();
    fetchDashboardStats();
  }, [partnerData?._id]);

  // Prepare data for StatCards based on real-time data
  const currentPeriodData = {
    totalApplications:
      summary.approved + summary.processing + summary.rejected || 0,
    pendingApplications: summary.processing || 0,
    approvedApplications: summary.approved || 0,
    totalLoanAmount: applications.reduce(
      (sum, app) => sum + (app.principle || app.amount || 0),
      0
    ),
  };

  // Use dashboard stats if available, otherwise calculate basic trends
  const trendsData = dashboardStats
    ? {
        totalApplicationsTrend: dashboardStats.totalApplicationsTrend || dashboardStats.trends?.totalApplications,
        pendingApplicationsTrend: dashboardStats.pendingApplicationsTrend || dashboardStats.trends?.pendingApplications,
        approvedApplicationsTrend: dashboardStats.approvedApplicationsTrend || dashboardStats.trends?.approvedApplications,
        totalLoanAmountTrend: dashboardStats.totalLoanAmountTrend || dashboardStats.trends?.totalLoanAmount,
      }
    : {
        totalApplicationsTrend: partnerData?.totalApplicationsTrend || 0,
        pendingApplicationsTrend: partnerData?.pendingApplicationsTrend || 0,
        approvedApplicationsTrend: partnerData?.approvedApplicationsTrend || 0,
        totalLoanAmountTrend: partnerData?.totalLoanAmountTrend || 0,
      };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-white mb-2">Error Loading Dashboard</h3>
        <p className="text-gray-400 mb-4 max-w-md">{error}</p>
        <button
          onClick={() => {
            fetchApplications();
            fetchDashboardStats();
          }}
          className="px-6 py-2 bg-[#FFCA20] text-black rounded-lg hover:bg-yellow-600 transition-colors font-medium"
        >
          Try Again
        </button>
      </div>
    );
  }

  console.log("Dashboard Stats:", dashboardStats);
  console.log("Current Period Data:", currentPeriodData);
  console.log("Trends Data:", trendsData);
  console.log("Applications:", applications);
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
        <p className="text-white mt-4">Loading dashboard...</p>
      </div>
    );
  }

  return (
    <>
      {/* Statistics */}
      <StatCards
        currentPeriodData={currentPeriodData}
        trendsData={trendsData}
        loading={loading}
      />

      {/* Applications Table */}
      <div className="mt-6">
        <ApplicationsTable applications={applications} loading={loading} />
      </div>
    </>
  );
}
